import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import GroupsIcon from "@mui/icons-material/Groups";
import PeopleIcon from "@mui/icons-material/People";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";
import { LocaleAndThemeToolbar } from "@/components/LocaleAndThemeToolbar";
import { LegalFooter } from "@/components/Layout/LegalFooter";

export default function Home() {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    {
      icon: <GroupsIcon sx={{ fontSize: 36, color: "primary.main" }} />,
      title: t("landing.feature1Title"),
      desc: t("landing.feature1Desc"),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 36, color: "secondary.main" }} />,
      title: t("landing.feature2Title"),
      desc: t("landing.feature2Desc"),
    },
    {
      icon: <CardMembershipIcon sx={{ fontSize: 36, color: "success.main" }} />,
      title: t("landing.feature3Title"),
      desc: t("landing.feature3Desc"),
    },
    {
      icon: <EventAvailableIcon sx={{ fontSize: 36, color: "warning.main" }} />,
      title: t("landing.feature4Title"),
      desc: t("landing.feature4Desc"),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Box
        component="header"
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h6"
          color="primary"
          sx={{
            fontWeight: 700,
          }}
        >
          {t("app.title")}
        </Typography>
        <LocaleAndThemeToolbar variant="page" />
      </Box>
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 4, md: 8 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 1,
              lineHeight: 1.15,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            {t("landing.hero")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              mb: 3,
              lineHeight: 1.15,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            {t("landing.heroHighlight")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.75,
            }}
          >
            {t("landing.heroSubtitle")}
          </Typography>

          <Box
            sx={{
              maxWidth: 360,
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <GoogleLoginButton />
            <Button
              component={Link}
              to="/preview"
              variant="outlined"
              size="medium"
              fullWidth
            >
              {t("preview.cta")}
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="text"
              size="medium"
              sx={{ textTransform: "none" }}
            >
              {t("landing.openSignInPage")}
            </Button>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
            }}
          >
            {t("landing.featuresTitle")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 4,
              maxWidth: 560,
              mx: "auto",
            }}
          >
            {t("landing.featuresSubtitle")}
          </Typography>
          <Grid container spacing={3}>
            {features.map((f, idx) => (
              <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                <Card
                  sx={{ height: "100%", textAlign: "left" }}
                  variant="outlined"
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>{f.icon}</Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {f.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {f.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      <LegalFooter />
    </Box>
  );
}
